import React from 'react';
import {Cell, Label, Pie, PieChart, ResponsiveContainer, Tooltip} from 'recharts';
import {Box, fade, Grid, Theme, useTheme} from '@material-ui/core';
import styled from 'styled-components';
import {useTranslation} from 'react-i18next';
import {Card, CardContent, CardTitle, Typography} from '../../../../ui-kit';
import {GrossIncomeByPaymentMethodQuery, PaymentIntegrationPartner, PaymentMethod} from '../../../../generated/graphql';
import {usePriceService} from '../../../../utils/hooks/priceService';
import {TArgs, translate} from '../../../../utils';
import {useUser} from '../../../../utils/hooks/auth';

const getPaymentMethodColor = (theme: Theme, paymentMethod: PaymentMethod | PaymentIntegrationPartner | null) => {
    switch (paymentMethod) {
        case PaymentMethod.Cash:
            return theme.palette.primary.main;

        case PaymentMethod.CreditCard:
            return theme.palette.additional.purple;

        case PaymentMethod.Other:
            return theme.palette.additional.orange;

        case PaymentIntegrationPartner.Qerko:
            return theme.palette.additional.green;

        default:
            return '#8993aa';
    }

};

const paymentMethodToString: Record<PaymentMethod | PaymentIntegrationPartner, TArgs> = {
    [PaymentMethod.Cash]: translate('Cash'),
    [PaymentMethod.CreditCard]: translate('Cards'),
    [PaymentMethod.Other]: translate('Other'),
    [PaymentIntegrationPartner.Qerko]: translate('Qerko')
};

const texts = {
    [PaymentMethod.Cash]: (value: string) => translate('Cash - {{value}}', {value}),
    [PaymentMethod.CreditCard]: (value: string) => translate('Cards - {{value}}', {value}),
    [PaymentMethod.Other]: (value: string) => translate('Other - {{value}}', {value}),
    [PaymentIntegrationPartner.Qerko]: (value: string) => translate('Qerko - {{value}}', {value}),
};

const Circle = styled.div<{ paymentMethod: PaymentMethod | PaymentIntegrationPartner }>`
  display: block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  ${({theme, paymentMethod}) => `
        background-color: ${getPaymentMethodColor(theme, paymentMethod)};
        margin-right: ${theme.spacing(1.5)}px;
    `}
`;

type GrossIncomeByPaymentMethod = GrossIncomeByPaymentMethodQuery['grossIncomeByPaymentMethod'];

const paymentMethods = Object.values(PaymentMethod);

const paymentIntegrations: PaymentIntegrationPartner[] = Object.values(PaymentIntegrationPartner);

const outermostCircleRadius = 100;

interface TotalIncomeViewProps {
    grossIncomeByPaymentMethod: GrossIncomeByPaymentMethod;
}

const TotalIncomeView = ({grossIncomeByPaymentMethod}: TotalIncomeViewProps) => {
    const theme = useTheme();
    const {user: {defaultCurrency: {currencyCode}}} = useUser();
    const {priceService} = usePriceService();
    const {t} = useTranslation();


    let total = 0;
    const keyedByPaymentMethod: Record<PaymentMethod, number | null> = {
        [PaymentMethod.Cash]: null,
        [PaymentMethod.CreditCard]: null,
        [PaymentMethod.Other]: null,
    };

    const keyedByPaymentIntegrationPartner: Record<PaymentIntegrationPartner, number | null> = {
        [PaymentIntegrationPartner.Qerko]: null,
    };

    grossIncomeByPaymentMethod.forEach(({income, paymentMethod, paymentIntegrationPartner}) => {
        total = priceService.add(total, income, currencyCode);
        if (paymentIntegrationPartner) {
            keyedByPaymentIntegrationPartner[paymentIntegrationPartner] = income;
            return;
        }

        keyedByPaymentMethod[paymentMethod] = income;
    });

    const hasData = grossIncomeByPaymentMethod.length > 0;

    const renderData = hasData
        ? grossIncomeByPaymentMethod
        : [{income: 100, paymentMethod: null}] as unknown as GrossIncomeByPaymentMethod;

    return (
        <Card fullHeight>
            <CardContent padding={3}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <CardTitle>{t('Total income')}</CardTitle>
                    </Grid>
                    <Grid item xs={12}>
                        <Box display='flex' justifyContent='center'>
                            <ResponsiveContainer width="80%" aspect={1}>
                                <PieChart>
                                    <Pie
                                        data={renderData}
                                        dataKey="income"
                                        nameKey="paymentMethod"
                                        cx="50%"
                                        cy="50%"
                                        strokeWidth={0}
                                        innerRadius={`${outermostCircleRadius - 25}%`}
                                        outerRadius={`${outermostCircleRadius - 5}%`}
                                    >
                                        {renderData.map(({paymentMethod, paymentIntegrationPartner}) => {
                                            const key = paymentIntegrationPartner
                                                ? `cell-${paymentMethod}-${paymentIntegrationPartner}`
                                                : `cell-${paymentMethod}`;
                                            return (
                                                <Cell
                                                    key={key}
                                                    fill={fade(
                                                        getPaymentMethodColor(theme, paymentIntegrationPartner || paymentMethod),
                                                        0.4
                                                    )}
                                                />
                                            );
                                        })}
                                    </Pie>
                                    <Pie
                                        data={renderData}
                                        dataKey="income"
                                        nameKey="paymentMethod"
                                        cx="50%"
                                        cy="50%"
                                        strokeWidth={0}
                                        innerRadius={`${outermostCircleRadius - 5}%`}
                                        outerRadius={`${outermostCircleRadius}%`}
                                    >
                                        {renderData.map(({paymentMethod, paymentIntegrationPartner}) => {
                                            const key = paymentIntegrationPartner
                                                ? `cell-${paymentMethod}-${paymentIntegrationPartner}`
                                                : `cell-${paymentMethod}`;
                                            return (
                                                <Cell
                                                    key={key}
                                                    fill={getPaymentMethodColor(theme, paymentIntegrationPartner || paymentMethod)}
                                                />
                                            );
                                        })}
                                        <Label fontSize={18} fontWeight={600} width={30} position="center">
                                            {priceService.toLocaleString(total)}
                                        </Label>
                                    </Pie>
                                    {hasData
                                        ? (
                                            <Tooltip
                                                allowEscapeViewBox={{x: false, y: false}}
                                                formatter={(value, name, entry) => {
                                                    // eslint-disable-next-line max-len
                                                    const paymentMethod: PaymentIntegrationPartner | PaymentMethod = entry.payload?.paymentIntegrationPartner || name;
                                                    const formatedName = t(...paymentMethodToString[paymentMethod]);
                                                    const formatedValue = priceService.toLocaleString(value as number);
                                                    const fraction = total > 0 ? value as number / total : 0;
                                                    const percentage = priceService.fractionToPercent(fraction, 2);
                                                    return [`${formatedValue} (${percentage}%)`, formatedName];
                                                }}
                                            />
                                        )
                                        : null}
                                </PieChart>
                            </ResponsiveContainer>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        {paymentMethods.map(paymentMethod => {
                            const value = keyedByPaymentMethod[paymentMethod];

                            if (value === null && paymentMethod === PaymentMethod.Other) {
                                return null;
                            }

                            const valueString = value === null ? t('N/A') : priceService.toLocaleString(value);
                            const translation = t(...texts[paymentMethod](valueString));
                            return (
                                <Box alignItems='center' mb={2} display='flex' key={paymentMethod}>
                                    <Circle paymentMethod={paymentMethod}/>
                                    <Typography>{translation}</Typography>
                                </Box>
                            );
                        })}
                        {paymentIntegrations.map(paymentIntegration => {
                            const value = keyedByPaymentIntegrationPartner[paymentIntegration];

                            if (value === null) {
                                return null;
                            }

                            const valueString = value === null ? t('N/A') : priceService.toLocaleString(value);
                            const translation = t(...texts[paymentIntegration](valueString));
                            return (
                                <Box alignItems='center' mb={2} display='flex' key={paymentIntegration}>
                                    <Circle paymentMethod={paymentIntegration}/>
                                    <Typography>{translation}</Typography>
                                </Box>
                            );
                        })}
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default TotalIncomeView;